import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useAppSelector } from '@/store/hooks'
import { selectMembers, selectIsWorkspacePro, selectStorageUsedBytes, selectStorageLimitBytes, selectIsStorageFull, selectUsageStats } from '@/store/workspace/workspaceSelectors'
import { selectProjectsAllIds } from '@/store/projects/projectsSelectors'
import { selectTasksAllIds } from '@/store/tasks/tasksSelectors'
import { FREE_PLAN_PROJECTS_LIMIT, FREE_PLAN_TASKS_LIMIT } from '@/const/constants'
import { fetchUsageStatsSaga } from '@/store/workspace/workspaceSlice'
import FormPanel from '@/components/ui/Form/components/FormPanel/FormPanel'
import FormDivider from '@/components/ui/Form/components/FormDivider/FormDivider'
import { TextNorm } from '@/components/ui/Text'
import WorkspaceSettingsTitle from '../WorkspaceSettingsTitle/WorkspaceSettingsTitle'
import WorkspaceUsageItem from './components/WorkspaceUsageItem/WorkspaceUsageItem'
import css from './WorkspaceSettingsModalUsage.module.css'

const formatBytes = (bytes: number): string => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

const WorkspaceSettingsModalUsage = () => {
    const dispatch = useDispatch()
    const isPro = useAppSelector(selectIsWorkspacePro)
    const members = useAppSelector(selectMembers)
    const projectIds = useAppSelector(selectProjectsAllIds)
    const taskIds = useAppSelector(selectTasksAllIds)
    const storageUsed = useAppSelector(selectStorageUsedBytes)
    const storageLimit = useAppSelector(selectStorageLimitBytes)
    const isStorageFull = useAppSelector(selectIsStorageFull)

    const membersCount = members.length
    const projectsCount = projectIds.length
    const projectsLimit = isPro ? null : FREE_PLAN_PROJECTS_LIMIT
    const projectsPercent = projectsLimit !== null ? Math.min(100, (projectsCount / projectsLimit) * 100) : undefined
    const isProjectsFull = projectsLimit !== null && projectsCount >= projectsLimit

    const tasksCount = taskIds.length
    const tasksLimit = isPro ? null : FREE_PLAN_TASKS_LIMIT
    const tasksPercent = tasksLimit !== null ? Math.min(100, (tasksCount / tasksLimit) * 100) : undefined
    const isTasksFull = tasksLimit !== null && tasksCount >= tasksLimit
    const tasksValue = tasksLimit !== null ? `${tasksCount} / ${tasksLimit}` : `${tasksCount}`

    const storagePercent = storageLimit !== null ? Math.min(100, (storageUsed / storageLimit) * 100) : undefined
    const usageStats = useAppSelector(selectUsageStats)

    useEffect(() => {
        dispatch(fetchUsageStatsSaga())
    }, [])

    const projectsValue = projectsLimit !== null
        ? `${projectsCount} / ${projectsLimit}`
        : `${projectsCount}`

    const storageValue = storageLimit !== null
        ? `${formatBytes(storageUsed)} / ${formatBytes(storageLimit)}`
        : formatBytes(storageUsed)

    return (
        <>
            <WorkspaceSettingsTitle>Usage</WorkspaceSettingsTitle>
            <FormDivider />
            <TextNorm className={css.subtitle}>Your storage and limits are based on your plan.</TextNorm>
            <FormPanel>
                <WorkspaceUsageItem label="Members" value={`${membersCount}`} />
                <div className={css.separator} />
                <WorkspaceUsageItem
                    label="Projects"
                    value={projectsValue}
                    percent={projectsPercent}
                    isFull={isProjectsFull}
                />
                <div className={css.separator} />
                <WorkspaceUsageItem
                    label="Tasks"
                    value={tasksValue}
                    percent={tasksPercent}
                    isFull={isTasksFull}
                />
                <div className={css.separator} />
                <WorkspaceUsageItem
                    label="Storage"
                    value={storageValue}
                    percent={storagePercent}
                    isFull={isStorageFull}
                />
            </FormPanel>
            <div style={{ marginTop: 24 }}><TextNorm className={css.subtitle}>API and integrations — current month.</TextNorm></div>
            <FormPanel>
                <WorkspaceUsageItem
                    label="API calls"
                    value={usageStats ? `${usageStats.apiCalls}` : '—'}
                />
                <div className={css.separator} />
                <WorkspaceUsageItem
                    label="Webhook events"
                    value={usageStats ? `${usageStats.webhookDeliveries}` : '—'}
                />
                <div className={css.separator} />
                <WorkspaceUsageItem
                    label="Agent tokens"
                    value={usageStats ? usageStats.agentTokens.toLocaleString() : '—'}
                />
            </FormPanel>
        </>
    )
}

export default WorkspaceSettingsModalUsage
